import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete, Phone, X } from 'lucide-react';
import { CountryCodeSelect } from '@components/CountryCodeSelect';
import { COUNTRY_CODE_REQUIRED_MESSAGE, sanitizePhoneInput } from '@/lib/phone';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { callBlockedReason, dialledNumber, placeOutboundCall } from '../outboundCall';

const KEYS: ReadonlyArray<readonly [string, string]> = [
  ['1', ''],
  ['2', 'ABC'],
  ['3', 'DEF'],
  ['4', 'GHI'],
  ['5', 'JKL'],
  ['6', 'MNO'],
  ['7', 'PQRS'],
  ['8', 'TUV'],
  ['9', 'WXYZ'],
];
const KEY_CLASS =
  'flex h-[58px] flex-col items-center justify-center rounded-[10px] border border-[var(--co-line)] bg-[var(--co-sunken)] ' +
  'text-[var(--co-ink)] transition hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]';

/** Dial any number. Agents pick the country themselves, or type a number starting with +. */
export function DialPad({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const isOnline = useVoiceAgentStore((s) => s.isOnline);
  const mediaUrl = useVoiceAgentStore((s) => s.mediaUrl);
  const onCall = useVoiceAgentStore((s) => !!s.activeCall || !!s.incomingCall);
  const [countryCode, setCountryCode] = useState('');
  const [entry, setEntry] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const number = dialledNumber(countryCode, entry);
  const needsCountry = !!entry.replace(/\D/g, '') && !number;
  const blocked = number ? callBlockedReason({ phone: number, isOnline, onCall, mediaUrl }) : null;

  const press = (key: string) => {
    setError(null);
    setEntry((current) => sanitizePhoneInput(current + key));
  };

  const call = async () => {
    if (!number || blocked || placing) return;
    setPlacing(true);
    setError(null);
    try {
      await placeOutboundCall({ customerPhone: number });
      onClose();
      navigate('/live-call/active');
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-[var(--co-raised)] px-5 py-4">
      <div className="flex flex-none items-center justify-between">
        <h2 className="text-[15px] font-bold text-[var(--co-ink)]">Call a number</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close keypad"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--co-ink-3)] hover:bg-[var(--co-selected)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form
        className="mt-4 flex flex-none gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void call();
        }}
      >
        <CountryCodeSelect value={countryCode} onChange={setCountryCode} />
        <input
          type="tel"
          value={entry}
          onChange={(event) => {
            setError(null);
            setEntry(sanitizePhoneInput(event.target.value));
          }}
          placeholder="Type or paste a number"
          aria-label="Phone number"
          className="min-w-0 flex-1 rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 text-[15px] tabular-nums text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none"
        />
      </form>

      <div className="mt-4 grid flex-none grid-cols-3 gap-2">
        {KEYS.map(([digit, letters]) => (
          <button key={digit} type="button" onClick={() => press(digit)} aria-label={digit} className={KEY_CLASS}>
            <span className="text-[20px] font-medium leading-none">{digit}</span>
            <span className="mt-1 h-3 text-[9.5px] tracking-[.1em] text-[var(--co-ink-3)]">{letters}</span>
          </button>
        ))}
        {/* A + only means something first. */}
        <button type="button" onClick={() => !entry && press('+')} disabled={!!entry} aria-label="Plus" className={`${KEY_CLASS} disabled:opacity-40`}>
          <span className="text-[20px] font-medium leading-none">+</span>
        </button>
        <button type="button" onClick={() => press('0')} aria-label="0" className={KEY_CLASS}>
          <span className="text-[20px] font-medium leading-none">0</span>
        </button>
        <button
          type="button"
          onClick={() => setEntry((current) => current.slice(0, -1))}
          disabled={!entry}
          aria-label="Delete"
          className={`${KEY_CLASS} disabled:opacity-40`}
        >
          <Delete className="h-5 w-5" />
        </button>
      </div>

      <div className="mt-3 min-h-[18px] flex-none text-[12px]">
        {error ? (
          <p role="alert" className="text-[var(--co-terracotta)]">
            {error}
          </p>
        ) : needsCountry ? (
          <p className="text-[var(--co-ink-3)]">{COUNTRY_CODE_REQUIRED_MESSAGE}</p>
        ) : blocked ? (
          <p className="text-[var(--co-ink-3)]">{blocked}</p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={call}
        disabled={!number || !!blocked || placing}
        className="mt-2 flex h-11 flex-none items-center justify-center gap-2 rounded-[10px] bg-[var(--co-brass-ink)] text-[14px] font-bold text-[var(--co-brass-on)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Phone className="h-4 w-4" />
        {placing ? 'Calling…' : 'Call'}
      </button>
    </div>
  );
}
